import React, { useState } from 'react';
import { db, storage } from "../firebase";
import { doc, setDoc, collection, getDocs, writeBatch, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Download, Upload, AlertCircle, Check, X } from "lucide-react";

const SeriesDataManager = ({ seriesList, onComplete }) => {
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState({});
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const toggleSeriesSelection = (seriesId) => {
    setSelectedSeries(prev => ({
      ...prev,
      [seriesId]: !prev[seriesId]
    }));
  };

  const selectAllSeries = () => {
    const allSelected = {};
    seriesList.forEach(series => {
      allSelected[series.docId] = true;
    });
    setSelectedSeries(allSelected);
  };

  const deselectAllSeries = () => {
    setSelectedSeries({});
  };

  const exportSeriesData = async () => {
    try {
      const selectedSeriesIds = Object.entries(selectedSeries)
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);

      if (selectedSeriesIds.length === 0) {
        setError("Please select at least one series to export");
        return;
      }

      const exportData = {};

      // Fetch selected series and their episodes
      for (const seriesId of selectedSeriesIds) {
        const series = seriesList.find(s => s.docId === seriesId);
        if (!series) continue;

        const episodesCollection = collection(db, "content", "main", "series", seriesId, "episodes");
        const episodesSnapshot = await getDocs(episodesCollection);
        const episodes = episodesSnapshot.docs.map(doc => ({
          ...doc.data(),
          docId: doc.id
        }));

        // Store the full thumbnail URL in the export data
        exportData[seriesId] = {
          series: {
            ...series,
            thumbnailUrl: series.thumbnail // Store the full URL
          },
          episodes
        };
      }

      // Create and download the JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `series_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setIsExportModalOpen(false);
      setSelectedSeries({});
    } catch (error) {
      console.error("Error exporting series data:", error);
      setError("Failed to export series data. Please try again.");
    }
  };

  const fetchAndUploadThumbnail = async (url, seriesId) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch thumbnail');
      
      const blob = await response.blob();
      const fileExt = url.split('.').pop().split('?')[0] || 'jpg';
      const fileName = `${seriesId}_${Date.now()}.${fileExt}`;
      const thumbnailRef = ref(storage, `thumbnails/${fileName}`);
      
      await uploadBytes(thumbnailRef, blob, {
        contentType: blob.type
      });
      
      return await getDownloadURL(thumbnailRef);
    } catch (error) {
      console.error("Error processing thumbnail:", error);
      return null;
    }
  };

  const importSeriesData = async (event) => {
    try {
      setImporting(true);
      setError("");
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          const batch = writeBatch(db);
          
          const totalSeries = Object.keys(importedData).length;
          setImportProgress({ current: 0, total: totalSeries });

          // Process each series and its episodes
          for (const [seriesId, data] of Object.entries(importedData)) {
            const { series, episodes } = data;
            
            // Handle thumbnail
            let thumbnailUrl = series.thumbnailUrl || series.thumbnail;
            if (thumbnailUrl) {
              const newThumbnailUrl = await fetchAndUploadThumbnail(thumbnailUrl, seriesId);
              if (newThumbnailUrl) {
                thumbnailUrl = newThumbnailUrl;
              }
            }

            // Create/Update series
            const seriesRef = doc(db, "content", "main", "series", series.docId);
            batch.set(seriesRef, {
              ...series,
              thumbnail: thumbnailUrl || '',
              importedAt: new Date().toISOString()
            });

            // Create/Update episodes
            for (const episode of episodes) {
              const episodeRef = doc(collection(seriesRef, "episodes"), episode.docId);
              batch.set(episodeRef, {
                ...episode,
                importedAt: new Date().toISOString()
              });
            }

            setImportProgress(prev => ({
              ...prev,
              current: prev.current + 1
            }));
          }

          await batch.commit();
          onComplete?.();
          setImporting(false);
          setError("");
          setImportProgress({ current: 0, total: 0 });
          event.target.value = ''; // Reset file input
        } catch (error) {
          console.error("Error processing import:", error);
          setError("Failed to import data. Please check the file format.");
          setImporting(false);
          setImportProgress({ current: 0, total: 0 });
        }
      };

      reader.readAsText(file);
    } catch (error) {
      console.error("Error importing series data:", error);
      setError("Failed to import series data. Please try again.");
      setImporting(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            disabled={importing}
          >
            <Download size={20} />
            Export Series
          </button>

          <label className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer">
            <Upload size={20} />
            Import Series
            <input
              type="file"
              accept=".json"
              onChange={importSeriesData}
              className="hidden"
              disabled={importing}
            />
          </label>
        </div>

        {error && (
          <div className="bg-red-500 text-white p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        {importing && (
          <div className="bg-blue-500 text-white p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>
              Importing data... {importProgress.current} of {importProgress.total} series processed
            </span>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Select Series to Export</h2>
              <button
                onClick={() => {
                  setIsExportModalOpen(false);
                  setSelectedSeries({});
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={selectAllSeries}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
              >
                Select All
              </button>
              <button
                onClick={deselectAllSeries}
                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded"
              >
                Deselect All
              </button>
            </div>

            <div className="space-y-2 mb-6">
              {seriesList.map(series => (
                <div
                  key={series.docId}
                  className="flex items-center gap-3 p-3 bg-gray-700 rounded hover:bg-gray-600 cursor-pointer"
                  onClick={() => toggleSeriesSelection(series.docId)}
                >
                  <div className={`w-6 h-6 rounded border ${selectedSeries[series.docId] ? 'bg-green-500 border-green-500' : 'border-gray-400'} flex items-center justify-center`}>
                    {selectedSeries[series.docId] && <Check size={16} className="text-white" />}
                  </div>
                  <span>{series.title}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsExportModalOpen(false);
                  setSelectedSeries({});
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={exportSeriesData}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                Export Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SeriesDataManager;